import { useState, useRef, useEffect } from 'react'

interface CodeEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  language?: 'html' | 'css' | 'javascript'
}

export default function CodeEditor({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  language = 'html'
}: CodeEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const highlightRef = useRef<HTMLDivElement>(null)
  const lineNumbersRef = useRef<HTMLDivElement>(null)
  const [lineCount, setLineCount] = useState(1)

  const handleScroll = () => {
    if (textareaRef.current && highlightRef.current && lineNumbersRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  useEffect(() => {
    const lines = value.split('\n').length
    setLineCount(Math.max(lines, 1))
  }, [value])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const start = e.currentTarget.selectionStart
      const end = e.currentTarget.selectionEnd
      const newValue = value.substring(0, start) + '  ' + value.substring(end)
      onChange(newValue)
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = start + 2
          textareaRef.current.selectionEnd = start + 2
        }
      }, 0)
    }
  }

  const highlightHTML = (code: string): string => {
    if (!code) return ''

    // ✅ replaceAll con string para caracteres simples
    let highlighted = code
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')

    // ✅ replaceAll con regex /g para patrones complejos
    highlighted = highlighted.replaceAll(
      /(&lt;!--[^-]*(-(?!->)[^-]*)*--&gt;)/g,
      '<span class="code-comment">$1</span>'
    )

    highlighted = highlighted.replaceAll(
      /(&lt;!DOCTYPE[^&]*&gt;)/gi,
      '<span class="code-doctype">$1</span>'
    )

    highlighted = highlighted.replaceAll(
      /(&lt;\/)([\w-]+)(&gt;)/g,
      '<span class="code-bracket">$1</span><span class="code-tag">$2</span><span class="code-bracket">$3</span>'
    )

    highlighted = highlighted.replaceAll(
      /(&lt;)([\w-]+)([^<]*)(&gt;|\/&gt;)/g,
      (_match: string, open: string, tag: string, attrs: string, close: string) => {
        const processedAttrs = attrs
          .replaceAll(
            /([\w-]+)(=)(&quot;|')([^"']*)(\3)/g,
            '<span class="code-attr">$1</span><span class="code-operator">$2</span><span class="code-string">$3$4$5</span>'
          )
          .replaceAll(
            /([\w-]+)(=)([^\s&]+)/g,
            '<span class="code-attr">$1</span><span class="code-operator">$2</span><span class="code-string">$3</span>'
          )
        return `<span class="code-bracket">${open}</span><span class="code-tag">${tag}</span>${processedAttrs}<span class="code-bracket">${close}</span>`
      }
    )

    highlighted = highlighted.replaceAll(
      /(&amp;[\w#]+;)/g,
      '<span class="code-entity">$1</span>'
    )

    return highlighted
  }

  const getHighlightedCode = () => {
    switch (language) {
      case 'html':
        return highlightHTML(value)
      default:
        // ✅ replaceAll con string para caracteres simples
        return value
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
    }
  }

  return (
    <div className="code-editor-container rounded-lg overflow-hidden border border-[#3c3c3c]">
      {/* Barra superior estilo VS Code */}
      <div className="code-editor-header flex items-center gap-2 px-4 py-2 bg-[#323233] border-b border-[#3c3c3c]">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <div className="w-3 h-3 rounded-full bg-[#28c840]" />
        </div>
        <span className="text-xs text-gray-400 ml-2 font-mono">
          {language.toUpperCase()}
        </span>
      </div>

      {/* Editor principal */}
      <div className="code-editor-body flex bg-[#1e1e1e] relative">
        {/* Numeros de linea */}
        <div
          ref={lineNumbersRef}
          className="code-line-numbers flex-shrink-0 py-3 px-2 text-right select-none overflow-hidden bg-[#1e1e1e] border-r border-[#3c3c3c]"
          style={{ minWidth: '50px' }}
        >
          {Array.from({ length: lineCount }, (_, i) => (
            <div key={i + 1} className="text-[#858585] text-sm font-mono leading-6 h-6">
              {i + 1}
            </div>
          ))}
        </div>

        {/* Area de codigo */}
        <div className="code-editor-content flex-1 relative min-h-[300px]">
          {/* Capa de resaltado de sintaxis */}
          <div
            ref={highlightRef}
            className="code-highlight absolute inset-0 p-3 overflow-auto pointer-events-none"
            style={{
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
              fontSize: '14px',
              lineHeight: '24px',
              color: '#d4d4d4'
            }}
            dangerouslySetInnerHTML={{ __html: getHighlightedCode() + '\n' }}
          />

          {/* Textarea invisible para edicion */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={handleScroll}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="code-textarea absolute inset-0 w-full h-full p-3 resize-none bg-transparent text-transparent caret-white outline-none disabled:cursor-not-allowed"
            style={{
              fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
              fontSize: '14px',
              lineHeight: '24px',
              caretColor: '#fff'
            }}
            spellCheck={false}
          />

          {/* Placeholder */}
          {!value && placeholder && (
            <div
              className="absolute top-3 left-3 text-[#6a6a6a] pointer-events-none"
              style={{
                fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                fontSize: '14px'
              }}
            >
              {placeholder}
            </div>
          )}
        </div>
      </div>

      {/* Barra inferior */}
      <div className="code-editor-footer flex items-center justify-between px-4 py-1 bg-[#007acc] text-white text-xs">
        <div className="flex items-center gap-4">
          <span>Ln {lineCount}, Col 1</span>
          <span>Spaces: 2</span>
        </div>
        <div className="flex items-center gap-4">
          <span>UTF-8</span>
          <span>{language.toUpperCase()}</span>
        </div>
      </div>

      {/* Estilos de resaltado de sintaxis */}
      <style>{`
        .code-editor-container .code-comment { color: #6a9955; font-style: italic; }
        .code-editor-container .code-doctype { color: #569cd6; }
        .code-editor-container .code-tag { color: #569cd6; }
        .code-editor-container .code-bracket { color: #808080; }
        .code-editor-container .code-attr { color: #9cdcfe; }
        .code-editor-container .code-operator { color: #d4d4d4; }
        .code-editor-container .code-string { color: #ce9178; }
        .code-editor-container .code-entity { color: #d7ba7d; }
        .code-editor-container .code-textarea::selection { background: #264f78; }
        .code-editor-container .code-highlight::selection,
        .code-editor-container .code-highlight *::selection { background: #264f78; }
      `}</style>
    </div>
  )
}